import type React from 'react';
import { motion } from 'framer-motion';
import { staggerCards, cardItem, fadeInUp } from '../utils/animations';
import Section from './ui/Section';
import SectionHeading from './ui/SectionHeading';
import Icon from './ui/Icon';
import { ADVANTAGES, TESTIMONIALS } from '../utils/constants';

const Advantages: React.FC = () => {
  return (
    <Section id="advantages" bgColor="primary-light">
      <SectionHeading
        title="Почему выбирают IT Joy?"
        subtitle="Наши клиенты ценят нас за надежность, прозрачность и индивидуальный подход"
      />

      {/* Advantages cards */}
      <motion.div
        className="mb-20 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        variants={staggerCards}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        {ADVANTAGES.map((advantage) => (
          <motion.div
            key={advantage.id}
            className="flex flex-col rounded-lg bg-white p-6 shadow-md transition-all duration-300 hover:shadow-lg"
            variants={cardItem}
          >
            <div className="mb-4 inline-flex rounded-full bg-primary/10 p-3 text-primary">
              <Icon name={advantage.icon} size={32} />
            </div>
            <h3 className="mb-3 text-xl font-bold text-gray-900">{advantage.title}</h3>
            <p className="text-gray-600">{advantage.description}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Testimonials section */}
      <div className="mb-8">
        <h3 className="mb-8 text-center text-2xl font-bold text-primary">
          Что говорят наши клиенты
        </h3>

        <div className="overflow-hidden">
          <motion.div
            className="flex gap-6 pb-8 overflow-x-auto md:grid md:grid-cols-3 md:overflow-visible"
            variants={staggerCards}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {TESTIMONIALS.map((testimonial) => (
              <motion.div
                key={testimonial.id}
                className="flex min-w-[300px] flex-col rounded-lg border border-gray-100 bg-white p-6 shadow-sm"
                variants={cardItem}
              >
                {/* Testimonial quote */}
                <div className="relative mb-8">
                  {/* Quote icon */}
                  <div className="absolute -left-2 -top-2 text-primary/10">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.5 20H7.5C6.83696 20 6.20107 19.7366 5.73223 19.2678C5.26339 18.7989 5 18.163 5 17.5V12.5C5 11.837 5.26339 11.2011 5.73223 10.7322C6.20107 10.2634 6.83696 10 7.5 10H12.5C13.163 10 13.7989 10.2634 14.2678 10.7322C14.7366 11.2011 15 11.837 15 12.5V27.5M32.5 20H27.5C26.837 20 26.2011 19.7366 25.7322 19.2678C25.2634 18.7989 25 18.163 25 17.5V12.5C25 11.837 25.2634 11.2011 25.7322 10.7322C26.2011 10.2634 26.837 10 27.5 10H32.5C33.163 10 33.7989 10.2634 34.2678 10.7322C34.7366 11.2011 35 11.837 35 12.5V27.5"
                        stroke="currentColor"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  <p className="italic text-gray-700">{testimonial.text}</p>
                </div>

                {/* Author info */}
                <div className="mt-auto flex items-center">
                  <div className="mr-4 h-12 w-12 overflow-hidden rounded-full bg-gray-200">
                    <img src={testimonial.image} alt={`${testimonial.author} Avatar`} className="h-full w-full object-cover" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.author}</p>
                    <p className="text-sm text-gray-500">{testimonial.position}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Statistics */}
      <motion.div
        className="rounded-lg bg-primary p-8 text-white"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-3">
          <div className="text-center">
            <p className="text-4xl font-bold text-secondary">20+</p>
            <p className="mt-2">Лет опыта</p>
          </div>

          <div className="text-center">
            <p className="text-4xl font-bold text-secondary">100+</p>
            <p className="mt-2">Реализованных проектов</p>
          </div>

          <div className="text-center">
            <p className="text-4xl font-bold text-secondary">24/7</p>
            <p className="mt-2">Техническая поддержка</p>
          </div>
        </div>
      </motion.div>
    </Section>
  );
};

export default Advantages;
